import FacilitiesSelectController from './facilities_select_controller'

export default class extends FacilitiesSelectController {
  static targets = ['facilitiesWrapper', 'select']
  static values = {
    url: { type: String, default: '/facilities' },
    organizationId: String,
    auditorId: { type: String, default: '' },
    selectAll: { type: Boolean, default: false },
    withSelectAllHeader: { type: Boolean, default: false }
  }

  declare readonly facilitiesWrapperTarget: HTMLElement
  declare hasFacilitiesWrapperTarget: boolean
  declare organizationIdValue: string
  declare auditorIdValue: string
  declare selectTarget: HTMLSelectElement
  declare hasSelectTarget: boolean
  declare selectAllValue: boolean
  declare withSelectAllHeaderValue: boolean

  private selectAllCheckbox: HTMLInputElement | null = null
  private readonly isSelectAllInProgress = false
  private skipSelectAllValueChanged = false

  connect (): void {
    if (!this.hasFacilitiesWrapperTarget || !this.hasSelectTarget) return

    super.connect()
    if (this.plugin === null || this.plugin === undefined) return

    if (this.withSelectAllHeaderValue) {
      setTimeout(() => this.createHeaderCheckbox(), 0)

      this.plugin.on('item_add', this.itemAdded.bind(this))
      this.plugin.on('item_remove', this.itemRemoved.bind(this))
      this.plugin.on('dropdown_open', this.updateSelectAllCheckboxState.bind(this))
    }
  }

  disconnect (): void {
    if (this.selectAllCheckbox !== null) {
      this.selectAllCheckbox.removeEventListener('change', this.handleHeaderCheckboxChange)
    }
    super.disconnect()
  }

  private createHeaderCheckbox (): void {
    const uniqueId = `ts-select-all-${crypto.randomUUID()}`
    const labelText = 'Select All'

    const container = document.createElement('div')
    container.className = 'ts-header-select-all-container flex items-center px-4 py-3 border-b border-subtle bg-sunken cursor-pointer'
    container.innerHTML = `
    <label for="${uniqueId}" class="ts-header-label flex items-center space-x-3 text-sm font-medium text-primary w-full cursor-pointer">
      <input
        type="checkbox"
        id="${uniqueId}"
        class="ts-header-checkbox form-checkbox lt-checkbox pointer-events-none"
        ${this.selectAllValue ? 'checked' : ''}
      />
      <span>${labelText}</span>
    </label>
  `

    const checkbox = container.querySelector<HTMLInputElement>('.ts-header-checkbox')
    if (checkbox === null) return

    checkbox.style.cursor = 'pointer'

    this.selectAllCheckbox = checkbox
    this.handleHeaderCheckboxChange = this.handleHeaderCheckboxChange.bind(this)
    this.selectAllCheckbox.addEventListener('change', this.handleHeaderCheckboxChange)

    container.addEventListener('click', (event: MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()

      if (!(event.target instanceof HTMLInputElement)) {
        checkbox.checked = !checkbox.checked
        checkbox.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    if (this.plugin?.dropdown != null) {
      this.plugin.dropdown.insertBefore(container, this.plugin.dropdown.firstChild ?? null)
    }
  }

  updateSelectAllCheckboxState (): void {
    if (this.plugin === null || this.plugin === undefined) return

    const selectedIds = this.plugin.getValue()
    const allOptionIds = Object.keys(this.plugin.options).map(String)
    const allSelected = allOptionIds.length > 0 && allOptionIds.every(id => selectedIds.includes(id))

    this.skipSelectAllValueChanged = true
    this.selectAllValue = allSelected

    if (this.selectAllCheckbox !== null) {
      this.selectAllCheckbox.checked = allSelected
      this.selectAllCheckbox.indeterminate = selectedIds.length > 0 && !allSelected
    }

    setTimeout(() => {
      this.skipSelectAllValueChanged = false
    }, 0)
  }

  private itemAdded (): void {
    if (this.isSelectAllInProgress) return
    this.updateSelectAllCheckboxState()
  }

  private itemRemoved (): void {
    if (this.isSelectAllInProgress) return
    this.updateSelectAllCheckboxState()
  }

  private handleHeaderCheckboxChange (event: Event): void {
    const checkbox = event.target as HTMLInputElement
    this.selectAllValue = checkbox.checked
  }

  selectElement (): HTMLSelectElement {
    return this.selectTarget
  }

  buildUrl (query: string): string {
    const baseUrl = super.buildUrl(query)
    const url = new URL(baseUrl, location.origin)

    if (this.selectAllValue) {
      const params = new URLSearchParams()
      params.set('all', 'true')
      params.set('q[organization_id_eq]', this.organizationIdValue)

      if (this.auditorIdValue !== '') {
        params.set('q[auditor_id_eq]', this.auditorIdValue)
      }

      url.search = params.toString()
    } else {
      url.searchParams.set('q[organization_id_eq]', this.organizationIdValue)
    }

    return url.toString()
  }

  changeCompany ({ target }: { target: HTMLInputElement }): void {
    this.organizationIdValue = target.value
  }

  changeAuditor ({ detail: { auditorId } }: { detail: { auditorId: string } }): void {
    this.auditorIdValue = auditorId
  }

  organizationIdValueChanged (): void {
    const isHidden = this.organizationIdValue === ''
    this.facilitiesWrapperTarget.classList.toggle('hidden', isHidden)
    if (!isHidden) this.clearAndReload()
  }

  auditorIdValueChanged (): void {
    this.organizationIdValueChanged()
  }

  selectAllValueChanged (): void {
    if (this.plugin === null || this.plugin === undefined || this.skipSelectAllValueChanged) {
      return
    }

    const allLoadedOptionIds = Object.keys(this.plugin.options).map(String)
    const currentlySelected = this.plugin.getValue()

    if (this.selectAllValue) {
      for (const id of allLoadedOptionIds) {
        if (!currentlySelected.includes(id)) {
          this.plugin.addItem(id, true)
        }
      }
    } else {
      for (const id of [...currentlySelected]) {
        this.plugin.removeItem(id, true)
      }
    }

    this.plugin.refreshOptions(false)
  }
}
